import { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Building2, Mail, Phone, DollarSign, Calendar, Target } from 'lucide-react';
import axios from 'axios';
import brand from '../config/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

const budgetRanges = [
  '$1,000 - $5,000',
  '$5,000 - $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  '$50,000+',
];

const adTypes = [
  'Banner Ads',
  'Sponsored Content',
  'Newsletter Placement',
  'Homepage Takeover',
  'Video Ads',
  'Native Advertising',
];

const durations = [
  '1 Week',
  '2 Weeks',
  '1 Month',
  '3 Months',
  '6 Months',
  '1 Year',
];

export default function AdvertiserPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    budget_range: '',
    ad_type: '',
    target_categories: [],
    campaign_duration: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryToggle = (slug) => {
    setFormData((prev) => ({
      ...prev,
      target_categories: prev.target_categories.includes(slug)
        ? prev.target_categories.filter((c) => c !== slug)
        : [...prev.target_categories, slug],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.target_categories.length === 0) {
      toast.error('Please select at least one target category');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/advertiser/request`, formData);
      toast.success('Your request has been submitted! We will contact you soon.');
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        budget_range: '',
        ad_type: '',
        target_categories: [],
        campaign_duration: '',
        message: '',
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="advertiser-page">
      <StockTicker />
      <Navbar />

      {/* Header */}
      <header className="py-16 sm:py-20 border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h1
            className="text-4xl md:text-5xl text-[#0A192F] mb-4"
            style={{ fontFamily: "'Libre Baskerville', serif" }}
          >
            Advertise With Us
          </h1>
          <p className="text-[#64748B] text-lg max-w-2xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Reach thousands of finance-focused readers. Partner with {brand.name} to promote your brand, 
            products, or services to our engaged audience.
          </p>
        </div>
      </header>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="bg-white p-8 rounded-none border border-[#E2E8F0]">
            <form onSubmit={handleSubmit} className="space-y-10">
              {/* Company Information */}
              <div>
                <h3
                  className="text-xl text-[#0A192F] mb-6 flex items-center"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  <Building2 className="w-5 h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                  Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Company Name *
                    </Label>
                    <Input
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
                      placeholder="Your Company"
                      data-testid="advertiser-company-name"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Contact Name *
                    </Label>
                    <Input
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
                      placeholder="John Doe"
                      data-testid="advertiser-contact-name"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3
                  className="text-xl text-[#0A192F] mb-6 flex items-center"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  <Mail className="w-5 h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                  Contact Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Email Address *
                    </Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
                      placeholder="contact@company.com"
                      data-testid="advertiser-email"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Phone Number
                    </Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
                      placeholder="+1 (555) 000-0000"
                      data-testid="advertiser-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Campaign Details */}
              <div>
                <h3
                  className="text-xl text-[#0A192F] mb-6 flex items-center"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  <DollarSign className="w-5 h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                  Campaign Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Budget Range
                    </Label>
                    <Select
                      value={formData.budget_range}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, budget_range: value }))}
                    >
                      <SelectTrigger className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none" data-testid="advertiser-budget">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E2E8F0] shadow-lg z-[100] rounded-none">
                        {budgetRanges.map((range) => (
                          <SelectItem key={range} value={range} className="text-[#1A1A1A] hover:bg-[#F0F4F8] cursor-pointer">
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Ad Type
                    </Label>
                    <Select
                      value={formData.ad_type}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, ad_type: value }))}
                    >
                      <SelectTrigger className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none" data-testid="advertiser-ad-type">
                        <SelectValue placeholder="Select ad type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E2E8F0] shadow-lg z-[100] rounded-none">
                        {adTypes.map((type) => (
                          <SelectItem key={type} value={type} className="text-[#1A1A1A] hover:bg-[#F0F4F8] cursor-pointer">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Campaign Duration */}
              <div>
                <h3
                  className="text-xl text-[#0A192F] mb-6 flex items-center"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  <Calendar className="w-5 h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                  Campaign Duration
                </h3>
                <Select
                  value={formData.campaign_duration}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, campaign_duration: value }))}
                >
                  <SelectTrigger className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none" data-testid="advertiser-duration">
                    <SelectValue placeholder="Select campaign duration" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0] shadow-lg z-[100] rounded-none">
                    {durations.map((duration) => (
                      <SelectItem key={duration} value={duration} className="text-[#1A1A1A] hover:bg-[#F0F4F8] cursor-pointer">
                        {duration}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target Categories */}
              <div>
                <h3
                  className="text-xl text-[#0A192F] mb-6 flex items-center"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  <Target className="w-5 h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                  Target Categories *
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {categories.map((cat) => (
                    <div key={cat.slug} className="flex items-center space-x-2">
                      <Checkbox
                        id={cat.slug}
                        checked={formData.target_categories.includes(cat.slug)}
                        onCheckedChange={() => handleCategoryToggle(cat.slug)}
                        className="border-[#E2E8F0] data-[state=checked]:bg-[#0A192F] data-[state=checked]:border-[#0A192F]"
                        data-testid={`advertiser-category-${cat.slug}`}
                      />
                      <Label htmlFor={cat.slug} className="text-sm text-[#1A1A1A] cursor-pointer" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {cat.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Additional Message
                </Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] min-h-[120px] rounded-none"
                  placeholder="Tell us about your campaign goals, target audience, or any specific requirements..."
                  data-testid="advertiser-message"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0A192F] text-white hover:bg-[#0A192F]/90 uppercase tracking-widest text-xs py-3 rounded-none"
                data-testid="advertiser-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
